import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X, Check, Star, Search } from 'lucide-react';
import './ReportsNative.css';

/**
 * Filter rail — the extensible "+ Filter" pattern for ReportFilterBar.
 *
 * <FilterRail filters={useReportFilters(CATALOG)} optionsCtx={{ rows }} />
 *
 * Renders the page's ACTIVE catalog filters as warm orange removable chips
 * with type-specific editor popovers, followed by a ghost "+ Filter" chip
 * that opens the searchable catalog. Everything is driven by the
 * useReportFilters hook (URL-serialized, page-declared definitions).
 */

function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  return { open, setOpen, ref };
}

/**
 * The popover CSS right-aligns to its chip (correct when the rail sits at the
 * bar's right edge), but a chip near the LEFT viewport edge would push the
 * 240–320px panel off-screen/under the sidebar. Flip alignment per-open based
 * on where the chip actually is.
 */
function popAlignStyle(anchorRef) {
  const el = anchorRef?.current;
  if (!el) return undefined;
  const rect = el.getBoundingClientRect();
  const width = 320; // max-width of .rn-colpicker-pop
  if (rect.right - width < 8 && rect.left + width <= window.innerWidth - 8) {
    return { left: 0, right: 'auto' };
  }
  return undefined;
}

function summarize(def) {
  const v = def.value;
  if (v === null || v === undefined || (Array.isArray(v) && !v.length)) return 'Any';
  switch (def.type) {
    case 'multi':
      return v.length === 1 ? String(v[0]) : `${v.length} selected`;
    case 'select': {
      const opt = (def.options || []).find((o) => o.value === v);
      return opt ? String(opt.label) : String(v);
    }
    case 'range': {
      const { min, max } = v || {};
      const u = def.unit || '';
      if (min != null && min !== '' && max != null && max !== '') return `${u}${min} – ${u}${max}`;
      if (min != null && min !== '') return `≥ ${u}${min}`;
      if (max != null && max !== '') return `≤ ${u}${max}`;
      return 'Any';
    }
    case 'rating':
      return `≥ ${v}★`;
    case 'text':
      return `“${v}”`;
    default:
      return String(v);
  }
}

function MultiEditor({ def, options, onChange }) {
  const [q, setQ] = useState('');
  const selected = Array.isArray(def.value) ? def.value : [];
  const shown = options.filter((o) => !q || String(o.label).toLowerCase().includes(q.toLowerCase()));
  const toggle = (value) =>
    onChange(selected.includes(value) ? selected.filter((s) => s !== value) : [...selected, value]);
  return (
    <>
      {options.length > 7 && (
        <div className="rn-fr-search">
          <Search />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${def.label.toLowerCase()}…`} />
        </div>
      )}
      <div className="rn-colpicker-list">
        {shown.map((o) => {
          const checked = selected.includes(o.value);
          return (
            <label key={String(o.value)} className="rn-colpicker-item">
              <span className={`rn-colpicker-box${checked ? ' rn-colpicker-box-on' : ''}`}>{checked && <Check />}</span>
              <input type="checkbox" checked={checked} onChange={() => toggle(o.value)} style={{ display: 'none' }} />
              {o.label}
            </label>
          );
        })}
        {!shown.length && <div className="rn-empty" style={{ padding: 12 }}>No matches</div>}
      </div>
    </>
  );
}

function SelectEditor({ def, options, onChange }) {
  return (
    <div className="rn-colpicker-list">
      {options.map((o) => (
        <label key={String(o.value)} className="rn-colpicker-item" onClick={() => onChange(def.value === o.value ? null : o.value)}>
          <span className={`rn-colpicker-box${def.value === o.value ? ' rn-colpicker-box-on' : ''}`}>
            {def.value === o.value && <Check />}
          </span>
          {o.label}
        </label>
      ))}
    </div>
  );
}

function RangeEditor({ def, onChange }) {
  const v = def.value || {};
  const set = (field, raw) => {
    const next = { ...v, [field]: raw === '' ? null : raw };
    onChange(next.min == null && next.max == null ? null : next);
  };
  return (
    <div className="rn-fr-range">
      <label>
        Min {def.unit || ''}
        <input type="number" value={v.min ?? ''} onChange={(e) => set('min', e.target.value)} placeholder="any" />
      </label>
      <span className="rn-fr-range-dash">–</span>
      <label>
        Max {def.unit || ''}
        <input type="number" value={v.max ?? ''} onChange={(e) => set('max', e.target.value)} placeholder="any" />
      </label>
    </div>
  );
}

function RatingEditor({ def, onChange }) {
  const max = def.max || 5;
  return (
    <div className="rn-fr-rating">
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          type="button"
          className={`rn-fr-star${def.value != null && n >= def.value ? ' rn-fr-star-on' : ''}`}
          title={`${n}+ stars`}
          onClick={() => onChange(def.value === n ? null : n)}
        >
          <Star />
        </button>
      ))}
      <span className="rn-fr-rating-lbl">{def.value != null ? `${def.value}+ stars` : 'Any rating'}</span>
    </div>
  );
}

function TextEditor({ def, onChange }) {
  return (
    <div className="rn-fr-search" style={{ margin: 10 }}>
      <Search />
      <input
        value={def.value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder={def.placeholder || `Contains…`}
      />
    </div>
  );
}

function ActiveFilterChip({ def, optionsCtx, onChange, onRemove }) {
  const { open, setOpen, ref } = usePopover();
  const options = useMemo(
    () => (typeof def.optionsFrom === 'function' ? def.optionsFrom(optionsCtx) : def.options) || [],
    [def, optionsCtx]
  );
  return (
    <span className="rn-colpicker" ref={ref}>
      <button
        type="button"
        className={`rn-fr-chip${def.value != null ? ' rn-fr-chip-set' : ''}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="rn-chip-lbl">{def.label}</span>
        {summarize(def)}
        <span
          className="rn-fr-chip-x"
          role="button"
          title={`Remove ${def.label} filter`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(def.key);
          }}
        >
          <X />
        </span>
      </button>
      {open && (
        <div className="rn-colpicker-pop" role="dialog" style={popAlignStyle(ref)}>
          <div className="rn-colpicker-head">{def.label}</div>
          {def.type === 'multi' && <MultiEditor def={def} options={options} onChange={(v) => onChange(def.key, v)} />}
          {def.type === 'select' && <SelectEditor def={def} options={options} onChange={(v) => onChange(def.key, v)} />}
          {def.type === 'range' && <RangeEditor def={def} onChange={(v) => onChange(def.key, v)} />}
          {def.type === 'rating' && <RatingEditor def={def} onChange={(v) => onChange(def.key, v)} />}
          {def.type === 'text' && <TextEditor def={def} onChange={(v) => onChange(def.key, v)} />}
        </div>
      )}
    </span>
  );
}

export function FilterRail({ filters, optionsCtx }) {
  const { open, setOpen, ref } = usePopover();
  const [q, setQ] = useState('');
  if (!filters) return null;
  const { activeDefs, availableDefs, addFilter, removeFilter, setFilterValue } = filters;
  const shown = availableDefs.filter((d) => !q || d.label.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      {activeDefs.map((def) => (
        <ActiveFilterChip
          key={def.key}
          def={def}
          optionsCtx={optionsCtx}
          onChange={setFilterValue}
          onRemove={removeFilter}
        />
      ))}
      {availableDefs.length > 0 && (
        <span className="rn-colpicker" ref={ref}>
          <button type="button" className="rn-fr-add" onClick={() => setOpen((v) => !v)} title="Add a filter">
            <Plus />
            Filter
          </button>
          {open && (
            <div className="rn-colpicker-pop" role="menu" style={popAlignStyle(ref)}>
              <div className="rn-colpicker-head">Add filter</div>
              {availableDefs.length > 6 && (
                <div className="rn-fr-search">
                  <Search />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search filters…" autoFocus />
                </div>
              )}
              <div className="rn-colpicker-list">
                {shown.map((def) => (
                  <button
                    key={def.key}
                    type="button"
                    className="rn-fr-cat-item"
                    onClick={() => {
                      addFilter(def.key);
                      setOpen(false);
                      setQ('');
                    }}
                  >
                    <span className="rn-fr-cat-label">{def.label}</span>
                    <span className="rn-fr-cat-type">{def.typeHint || def.type}</span>
                  </button>
                ))}
                {!shown.length && <div className="rn-empty" style={{ padding: 12 }}>No matches</div>}
              </div>
              <div className="rn-fr-foot">Saved filter sets — coming soon</div>
            </div>
          )}
        </span>
      )}
    </>
  );
}

export default FilterRail;
